import React, { useState } from "react";
import { useAuth } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import { User, Bell, Shield, Users, Crown, Zap } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProfileTab } from "../components/settings/ProfileTab";
import { NotificationsTab } from "../components/settings/NotificationsTab";
import { SecurityTab } from "../components/settings/SecurityTab";
import { DelegatesTab } from "../components/settings/DelegatesTab";
import { SubscriptionTab } from "../components/settings/SubscriptionTab";
import { AIUsageTab } from "../components/settings/AIUsageTab";

export default function SettingsPage() {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    department: user?.department || "",
  });

  const [notifications, setNotifications] = useState({
    emailRenewals: true, emailApprovals: true, emailExpiration: true,
    inAppRenewals: true, inAppApprovals: true, reminderDays: "30",
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success("Profile updated successfully");
    setSaving(false);
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success("Notification preferences saved");
    setSaving(false);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Settings <HelpBubble tip="Organisation profile, subscription plan, e-signature provider, AI usage monitoring and per-user limits, premium features, and account security." /></h1>
        <p className="text-slate-500 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="profile" className="gap-2"><User size={16} /> Profile</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2"><Bell size={16} /> Notifications</TabsTrigger>
          <TabsTrigger value="security" className="gap-2"><Shield size={16} /> Security</TabsTrigger>
          <TabsTrigger value="delegates" className="gap-2"><Users size={16} /> My Delegates</TabsTrigger>
          <TabsTrigger value="subscription" className="gap-2" data-testid="subscription-settings-tab"><Crown size={16} /> Subscription</TabsTrigger>
          {(user?.role === "superuser" || user?.role === "admin") && (
            <TabsTrigger value="ai-usage" className="gap-2" data-testid="ai-usage-settings-tab"><Zap size={16} /> AI Usage</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile">
          <ProfileTab user={user} profileData={profileData} setProfileData={setProfileData} saving={saving} onSave={handleSaveProfile} />
        </TabsContent>
        <TabsContent value="notifications">
          <NotificationsTab notifications={notifications} setNotifications={setNotifications} saving={saving} onSave={handleSaveNotifications} />
        </TabsContent>
        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>
        <TabsContent value="delegates">
          <DelegatesTab />
        </TabsContent>
        <TabsContent value="subscription">
          <SubscriptionTab />
        </TabsContent>
        <TabsContent value="ai-usage">
          <AIUsageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
